/**
 * Métadonnées partagées du module Parc matériel (libellés FR, icônes lucide) +
 * petits helpers de formatage. Source unique réutilisée par la liste du parc,
 * la fiche, les formulaires, le sélecteur de type et les alertes dashboard.
 *
 * Aligné sur les enums `@champ-erp/contracts` (typeMaterielSchema,
 * modeDetentionSchema, typeEntretienSchema, categoriePlanSchema).
 * Icône + libellé toujours (jamais couleur seule : accessibilité terrain).
 */

import type {
  CategoriePlan,
  ModeDetention,
  TypeEntretien,
  TypeMateriel,
} from "@champ-erp/contracts";
import {
  Battery,
  Box,
  Droplets,
  Forklift,
  Gauge,
  Grape,
  Hammer,
  Leaf,
  Scissors,
  ShieldCheck,
  SprayCan,
  Tractor,
  Trees,
  Truck,
  Waypoints,
  Wind,
  Wrench,
} from "lucide-react";
import type { ReactNode } from "react";

export interface TypeMaterielMeta {
  /** Libellé français métier affiché à l'utilisateur. */
  libelle: string;
  /** Icône lucide (jamais couleur seule : icône + libellé). */
  icone: (size: number) => ReactNode;
  /** true pour les pulvérisateurs portés/traînés (contrôle réglementaire). */
  estPulverisateurControle: boolean;
}

/**
 * Ordre d'affichage des types dans la grille de saisie : engins emblématiques
 * du RM Champagne en tête (enjambeur, tracteur vigneron, pulvé), `autre` en fin.
 */
export const TYPES_MATERIEL: TypeMateriel[] = [
  "enjambeur",
  "tracteur_vigneron",
  "tracteur_interligne",
  "pulverisateur_porte",
  "pulverisateur_traine",
  "pulverisateur_porte_dos",
  "pretailleuse",
  "rogneuse",
  "effeuilleuse",
  "broyeur_sarments",
  "interceps",
  "lieuse",
  "secateur_electrique",
  "treuil",
  "chariot_elevateur",
  "remorque",
  "pompe",
  "pressoir",
  "autre",
];

export const TYPE_MATERIEL_META: Record<TypeMateriel, TypeMaterielMeta> = {
  enjambeur: {
    libelle: "Enjambeur",
    icone: (s) => <Tractor size={s} />,
    estPulverisateurControle: false,
  },
  tracteur_vigneron: {
    libelle: "Tracteur vigneron",
    icone: (s) => <Tractor size={s} />,
    estPulverisateurControle: false,
  },
  tracteur_interligne: {
    libelle: "Tracteur interligne",
    icone: (s) => <Tractor size={s} />,
    estPulverisateurControle: false,
  },
  chariot_elevateur: {
    libelle: "Chariot élévateur",
    icone: (s) => <Forklift size={s} />,
    estPulverisateurControle: false,
  },
  pulverisateur_porte: {
    libelle: "Pulvérisateur porté",
    icone: (s) => <SprayCan size={s} />,
    estPulverisateurControle: true,
  },
  pulverisateur_traine: {
    libelle: "Pulvérisateur traîné",
    icone: (s) => <SprayCan size={s} />,
    estPulverisateurControle: true,
  },
  pulverisateur_porte_dos: {
    libelle: "Pulvérisateur à dos",
    icone: (s) => <SprayCan size={s} />,
    estPulverisateurControle: false,
  },
  pretailleuse: {
    libelle: "Prétailleuse",
    icone: (s) => <Scissors size={s} />,
    estPulverisateurControle: false,
  },
  rogneuse: {
    libelle: "Rogneuse / écimeuse",
    icone: (s) => <Scissors size={s} />,
    estPulverisateurControle: false,
  },
  effeuilleuse: {
    libelle: "Effeuilleuse",
    icone: (s) => <Leaf size={s} />,
    estPulverisateurControle: false,
  },
  broyeur_sarments: {
    libelle: "Broyeur à sarments",
    icone: (s) => <Trees size={s} />,
    estPulverisateurControle: false,
  },
  interceps: {
    libelle: "Outil interceps",
    icone: (s) => <Waypoints size={s} />,
    estPulverisateurControle: false,
  },
  lieuse: {
    libelle: "Lieuse / attacheuse",
    icone: (s) => <Leaf size={s} />,
    estPulverisateurControle: false,
  },
  secateur_electrique: {
    libelle: "Sécateur électrique",
    icone: (s) => <Battery size={s} />,
    estPulverisateurControle: false,
  },
  treuil: {
    libelle: "Treuil de coteau",
    icone: (s) => <Wind size={s} />,
    estPulverisateurControle: false,
  },
  pressoir: {
    libelle: "Pressoir",
    icone: (s) => <Grape size={s} />,
    estPulverisateurControle: false,
  },
  pompe: {
    libelle: "Pompe / pompage",
    icone: (s) => <Droplets size={s} />,
    estPulverisateurControle: false,
  },
  remorque: {
    libelle: "Remorque / benne",
    icone: (s) => <Truck size={s} />,
    estPulverisateurControle: false,
  },
  autre: {
    libelle: "Autre matériel",
    icone: (s) => <Box size={s} />,
    estPulverisateurControle: false,
  },
};

/** Libellé d'un type (fallback sur le code brut si type inconnu hors enum). */
export function libelleTypeMateriel(type: string): string {
  return TYPE_MATERIEL_META[type as TypeMateriel]?.libelle ?? type;
}

/** Icône d'un type (fallback sur une icône neutre si type inconnu). */
export function iconeTypeMateriel(type: string, size: number): ReactNode {
  const meta = TYPE_MATERIEL_META[type as TypeMateriel];
  return meta ? meta.icone(size) : <Box size={size} />;
}

/** Le type est-il un pulvérisateur porté/traîné soumis au contrôle réglementaire ? */
export function estPulveControle(type: string): boolean {
  return TYPE_MATERIEL_META[type as TypeMateriel]?.estPulverisateurControle ?? false;
}

// ────────────────────────────────────────────────────────────────────────────
// Mode de détention
// ────────────────────────────────────────────────────────────────────────────

export const MODE_DETENTION_LABEL: Record<ModeDetention, string> = {
  propre: "En propre",
  cuma: "CUMA",
  copropriete: "Copropriété",
  location: "Location / leasing",
};

export const MODES_DETENTION: ModeDetention[] = ["propre", "cuma", "copropriete", "location"];

export function libelleModeDetention(mode: string): string {
  return MODE_DETENTION_LABEL[mode as ModeDetention] ?? mode;
}

// ────────────────────────────────────────────────────────────────────────────
// Type d'entretien (grille de saisie + libellé timeline)
// ────────────────────────────────────────────────────────────────────────────

export interface TypeEntretienMeta {
  libelle: string;
  icone: (size: number) => ReactNode;
}

/** Ordre de la grille : relevé en premier (le geste terrain le plus fréquent). */
export const TYPES_ENTRETIEN: TypeEntretien[] = [
  "releve_compteur",
  "revision",
  "vidange",
  "controle_reglementaire",
  "reparation",
  "autre",
];

export const TYPE_ENTRETIEN_META: Record<TypeEntretien, TypeEntretienMeta> = {
  releve_compteur: { libelle: "Relevé compteur", icone: (s) => <Gauge size={s} /> },
  revision: { libelle: "Révision", icone: (s) => <Wrench size={s} /> },
  vidange: { libelle: "Vidange", icone: (s) => <Droplets size={s} /> },
  controle_reglementaire: { libelle: "Contrôle pulvé", icone: (s) => <ShieldCheck size={s} /> },
  reparation: { libelle: "Réparation", icone: (s) => <Hammer size={s} /> },
  autre: { libelle: "Autre", icone: (s) => <Box size={s} /> },
};

export function libelleTypeEntretien(type: string): string {
  return TYPE_ENTRETIEN_META[type as TypeEntretien]?.libelle ?? type;
}

// ────────────────────────────────────────────────────────────────────────────
// Catégorie de plan d'entretien
// ────────────────────────────────────────────────────────────────────────────

export const CATEGORIE_PLAN_LABEL: Record<CategoriePlan, string> = {
  revision: "Révision",
  vidange: "Vidange",
  controle_reglementaire: "Contrôle réglementaire",
  autre: "Autre",
};

export const CATEGORIES_PLAN: CategoriePlan[] = [
  "revision",
  "vidange",
  "controle_reglementaire",
  "autre",
];

export function libelleCategoriePlan(categorie: string): string {
  return CATEGORIE_PLAN_LABEL[categorie as CategoriePlan] ?? categorie;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers de formatage (heures, coût, désignation, dates) — partagés UI
// ────────────────────────────────────────────────────────────────────────────

/**
 * Formate un compteur d'heures (string `numeric` Drizzle ou number) en français
 * « 1 240,5 h ». Retourne `null` si la valeur est absente / non finie.
 */
export function formatHeures(valeur: string | number | null): string | null {
  if (valeur === null) return null;
  const n = typeof valeur === "string" ? Number.parseFloat(valeur) : valeur;
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h`;
}

/** Formate un coût (string/number) en « 180,00 € ». `null` si absent. */
export function formatCout(valeur: string | number | null): string | null {
  if (valeur === null) return null;
  const n = typeof valeur === "string" ? Number.parseFloat(valeur) : valeur;
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

/**
 * Nom d'affichage d'un matériel : sa désignation libre si renseignée, sinon
 * « Marque Modèle », sinon le libellé du type. Jamais vide.
 */
export function nomMateriel(materiel: {
  type: string;
  designation: string | null;
  marque: string | null;
  modele: string | null;
}): string {
  if (materiel.designation) return materiel.designation;
  const marqueModele = [materiel.marque, materiel.modele].filter(Boolean).join(" ");
  if (marqueModele) return marqueModele;
  return libelleTypeMateriel(materiel.type);
}
